import { RotateCcwIcon } from "lucide-react";
import {
  Outlet,
  createFileRoute,
  redirect,
  useCanGoBack,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { useSettingsRestore } from "../components/settings/SettingsPanels";
import { SettingsBreadcrumb } from "../components/settings/SettingsBreadcrumb";
import type { SettingsPath } from "../components/settings/settingsSearch";
import { useUiStateStore } from "../uiStateStore";
import { resolveRoutedSettingsRelocation, useWorldView } from "../neucleos/worldView";
import { useThreadShells } from "../state/entities";
import { Button } from "../components/ui/button";
import { SidebarInset } from "../components/ui/sidebar";
import { isElectron } from "../env";
import { shellOpensFor } from "../authGate";
import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";

function RestoreDefaultsButton({ onRestored }: { onRestored: () => void }) {
  const { changedSettingLabels, restoreDefaults } = useSettingsRestore(onRestored);

  return (
    <Button
      size="xs"
      variant="ghost"
      disabled={changedSettingLabels.length === 0}
      onClick={() => void restoreDefaults()}
    >
      <RotateCcwIcon className="mx-1 size-3.5" />
      Restore defaults
    </Button>
  );
}

function SettingsContentLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const [restoreSignal, setRestoreSignal] = useState(0);
  const showRestoreDefaults = location.pathname === "/settings/general";
  const recordLastSettingsSection = useUiStateStore((state) => state.setLastSettingsSection);
  // The store ignores unknown and nav-hidden paths, so /settings/diagnostics
  // and deep links into hidden pages never become the default landing section.
  useEffect(() => {
    recordLastSettingsSection(location.pathname.replace(/\/+$/, ""));
  }, [location.pathname, recordLastSettingsSection]);
  const handleRestored = () => setRestoreSignal((value) => value + 1);
  const navigateBackWithinApp = useCallback(() => {
    if (canGoBack) {
      window.history.back();
      return;
    }
    void navigate({ to: "/" });
  }, [canGoBack, navigate]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === "Escape") {
        event.preventDefault();

        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement) {
          activeElement.blur();
        }

        navigateBackWithinApp();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [navigateBackWithinApp]);

  return (
    <SidebarInset className="h-full min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground isolate">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background text-foreground">
        {!isElectron && (
          <header
            className={cn(
              "flex h-[var(--workspace-topbar-height)] min-h-[var(--workspace-topbar-height)] shrink-0 items-center px-3 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none sm:px-5",
              COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
            )}
          >
            <div className="flex w-full items-center gap-2">
              <SettingsBreadcrumb pathname={location.pathname} />
              {showRestoreDefaults ? (
                <div className="ms-auto flex items-center gap-2">
                  <RestoreDefaultsButton onRestored={handleRestored} />
                </div>
              ) : null}
            </div>
          </header>
        )}

        {isElectron && (
          <div
            className={cn(
              "drag-region flex h-[52px] shrink-0 items-center px-5 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none wco:h-[env(titlebar-area-height)] wco:pr-[calc(100vw-env(titlebar-area-width)-env(titlebar-area-x)+1em)]",
              COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
            )}
          >
            <div className="flex w-full items-center gap-2">
              <SettingsBreadcrumb pathname={location.pathname} />
              {showRestoreDefaults ? (
                <div className="ms-auto flex items-center gap-2">
                  <RestoreDefaultsButton onRestored={handleRestored} />
                </div>
              ) : null}
            </div>
          </div>
        )}

        <div key={restoreSignal} className="min-h-0 flex flex-1 flex-col">
          <Outlet />
        </div>
      </div>
    </SidebarInset>
  );
}

function SettingsRouteLayout() {
  const { state } = useWorldView();
  const shells = useThreadShells();
  const navigate = useNavigate();
  // The routed settings page is Local chrome (WS8): a ready World relocates
  // it into that World's restored room, whose Canvas hosts Settings as a tab
  // (WS5). A loading or unreadable room keeps this page rendered and
  // reachable so a broken connection can be fixed here.
  const relocation = resolveRoutedSettingsRelocation({ room: state, shells });
  useEffect(() => {
    if (relocation === null) return;
    if (relocation.kind === "thread") {
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: relocation.environmentId, threadId: relocation.threadId },
        replace: true,
      } as never);
      return;
    }
    void navigate({
      to: "/session/$sessionId",
      params: { sessionId: relocation.sessionId },
      replace: true,
    } as never);
  }, [navigate, relocation]);
  if (relocation !== null) return null;
  return <SettingsContentLayout />;
}

export function resolveSettingsDefaultRedirect(input: {
  readonly pathname: string;
  readonly search: Readonly<Record<string, unknown>>;
  readonly lastSettingsSection?: SettingsPath | null;
}) {
  if (input.pathname !== "/settings") return null;
  return {
    to: input.lastSettingsSection ?? ("/settings/general" as const),
    replace: true,
    search: input.search,
  };
}

export const Route = createFileRoute("/settings")({
  beforeLoad: async ({ context, location }) => {
    if (!shellOpensFor(context.authGateState.status)) {
      throw redirect({ to: "/pair", replace: true });
    }

    const defaultRedirect = resolveSettingsDefaultRedirect({
      pathname: location.pathname,
      search: location.search,
      lastSettingsSection: useUiStateStore.getState().lastSettingsSection,
    });
    if (defaultRedirect) throw redirect(defaultRedirect);
  },
  component: SettingsRouteLayout,
});
